import React from 'react';
import { PortableText } from '@portabletext/react';
import type { PortableTextBlock as PortableTextBlockType } from '@sanity/types';
import { urlBuilder } from '@/utils';
import Video from '@/components/Video';
import { ImageWithAlt } from '@/types/ImageWithAltInterface';
import { Link as GatsbyLink } from 'gatsby';
import { BasicBlock, HeadingBlock } from '@/types/SanityTypes';

interface RichTextProps {
  value: PortableTextBlockType | BasicBlock | HeadingBlock;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';
  className?: string | undefined;
}

function getStyle(tag: string) {
  switch (tag) {
    case 'h1':
      return 'scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl';
    case 'h2':
      return 'scroll-m-20 text-3xl font-bold tracking-tight first:mt-0';
    case 'h3':
      return 'scroll-m-20 text-2xl font-semibold tracking-tight';
    case 'h4':
      return 'scroll-m-20 text-xl font-semibold tracking-tight';
    case 'h5':
      return 'scroll-m-20 text-lg font-semibold tracking-tight';
    case 'h6':
      return 'scroll-m-20 text-base font-semibold tracking-tight';
    case 'p':
      return 'leading-7 text-lg';
    default: // Assume paragraph
      return 'leading-7 text-lg';
  }
}

interface LinkInterface {
  children: React.ReactNode[];
  value: {
    url: string;
    reference: any;
  };
}

const portableTextComponents = {
  block: {
    normal: ({ children }: { children: React.ReactNode }) => (
      <p className='mb-4 last:mb-0'>{children}</p>
    ),
    h1: ({ children }: { children: React.ReactNode }) => (
      <h1 className='mb-6 text-4xl font-bold'>{children}</h1>
    ),
    h2: ({ children }: { children: React.ReactNode }) => (
      <h2 className='mb-4 text-3xl font-semibold'>{children}</h2>
    ),
    h3: ({ children }: { children: React.ReactNode }) => (
      <h3 className='mb-3 text-2xl font-semibold'>{children}</h3>
    ),
    h4: ({ children }: { children: React.ReactNode }) => (
      <h4 className='mb-2 text-xl font-semibold'>{children}</h4>
    ),
    blockquote: ({ children }: { children: React.ReactNode }) => (
      <blockquote className='my-6 border-l-4 border-gray-300 pl-4 italic'>{children}</blockquote>
    ),
  },
  list: {
    bullet: ({ children }: { children: React.ReactNode }) => (
      <ul className='mb-4 list-outside list-disc space-y-2 pl-6'>{children}</ul>
    ),
    number: ({ children }: { children: React.ReactNode }) => (
      <ol className='mb-4 list-outside list-decimal space-y-2 pl-6'>{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }: { children: React.ReactNode }) => <li className='pl-2'>{children}</li>,
    number: ({ children }: { children: React.ReactNode }) => <li className='pl-2'>{children}</li>,
  },
  marks: {
    externalLink: ({ children, value }: LinkInterface) => {
      const href = value.url || '';
      const rel = href.startsWith('/') ? undefined : 'noreferrer noopener';
      return (
        <a
          className='underline'
          target='_blank'
          href={href}
          rel={rel}
        >
          {children}
        </a>
      );
    },
    internalLink: ({ children, value }: LinkInterface) => {
      const href = urlBuilder(value.reference);
      return (
        <GatsbyLink
          className='underline'
          to={href}
        >
          {children}
        </GatsbyLink>
      );
    },
    strong: ({ children }: { children: React.ReactNode }) => (
      <strong className='font-semibold'>{children}</strong>
    ),
    em: ({ children }: { children: React.ReactNode }) => <em className='italic'>{children}</em>,
  },
  types: {
    video: ({ value }: { value: { videoUrl: string; thumbnail: ImageWithAlt } }) => {
      return (
        <Video
          videoUrl={value.videoUrl}
          thumbnail={value.thumbnail}
        />
      );
    },
  },
};

export const RichText = ({ value, as, className = '' }: RichTextProps) => {
  const style = as ? getStyle(as) : getStyle('p');

  // Convert Sanity-generated array types to PortableTextBlock if needed
  const portableTextValue: PortableTextBlockType = value as PortableTextBlockType;

  return (
    <div className={`${style} ${className}`}>
      <PortableText
        value={portableTextValue}
        components={portableTextComponents}
      />
    </div>
  );
};

export default RichText;
